import { CreateMessageFormData } from '@/types/admin/messageFormTypes';
import { IMessageResident, ILeaseCustomer } from '@/types/admin/messages';
import { logger } from '@/utils/common/logger';

// Build FormData for create-conversation, shared by mobile and desktop
export const buildCreateConversationFormData = (
  formData: CreateMessageFormData,
  selectedPropertyId: number
): FormData => {
  const fd = new FormData();

  // Basic fields (messageType decided after recipients partition)
  fd.append('subject', formData.subject);
  fd.append('messageContent', formData.messageContent);
  fd.append('sendEmail', formData.sendEmail.toString());
  fd.append('isAdmin', 'true');
  fd.append('propertyId', selectedPropertyId.toString());

  // Recipients processing (aligned with admin-web)
  const recipientLeaseCustomers = (formData.recipients || []).flatMap(
    (r: IMessageResident) => r.leaseCustomers?.[0]
  ) as unknown as ILeaseCustomer[];

  const assignedRecipients = recipientLeaseCustomers
    .filter((r: ILeaseCustomer) => r?.account && Object.keys(r.account || {}).length > 0)
    .map((r: ILeaseCustomer) => r.id as number);

  const unassignedRecipients = recipientLeaseCustomers
    .filter((r: ILeaseCustomer) => !r?.account || Object.keys(r.account || {}).length <= 0)
    .map((r: ILeaseCustomer) => r.id as number);

  fd.append('recipients', JSON.stringify(assignedRecipients));
  fd.append('unassignedRecipients', JSON.stringify(unassignedRecipients));

  // Final message type: if all are unassigned, force singleChat to avoid duplicate conversations
  const finalMessageType = assignedRecipients.length === 0 && unassignedRecipients.length > 0
    ? 'singleChat'
    : formData.messageType;
  fd.append('messageType', finalMessageType);

  // Mismatched guarantors mapping (ids array)
  if (formData.mismatchedGuarantors && formData.mismatchedGuarantors.length > 0) {
    const mismatchedGuarantorIds = formData.mismatchedGuarantors
      .map((r: any) => r?.leaseCustomers?.[0]?.account?.id)
      .filter((id: any) => typeof id === 'number');
    if (mismatchedGuarantorIds.length) {
      fd.append('mismatchedGuarantors', JSON.stringify(mismatchedGuarantorIds));
    }
  }

  // Files: on web append the real File if present at file.file; on native append { uri, type, name }
  formData.files?.forEach((file: any) => {
    const webFile = file?.file;
    if (webFile && typeof webFile === 'object' && typeof webFile.name === 'string') {
      try {
        fd.append('files', webFile as Blob);
        return;
      } catch (e) {
        logger.warn('messageFormDataWebFileAppendFailed', e);
      }
    }
    if (file?.uri) {
      fd.append('files', {
        uri: file.uri,
        type: file.type,
        name: file.name,
      } as unknown as Blob);
    }
  });

  return fd;
};


